'use client';

import { motion, AnimatePresence } from 'framer-motion';
import { X, Maximize2, Volume2, VolumeX } from 'lucide-react';
import { useState, useRef, useEffect } from 'react';

interface StickyPiPProps {
  isVisible: boolean;
  onClose: () => void;
  onExpand: () => void;
  videoSrc: string;
}

export default function StickyPiP({ isVisible, onClose, onExpand, videoSrc }: StickyPiPProps) {
  const [isMuted, setIsMuted] = useState(true);
  const [isHovered, setIsHovered] = useState(false);
  const [showTooltip, setShowTooltip] = useState(false);
  const videoRef = useRef<HTMLVideoElement>(null);
  const hasTriggeredTooltip = useRef(false);

  // Sync mute state with video element
  useEffect(() => {
    if (videoRef.current) {
      videoRef.current.muted = isMuted;
    }
  }, [isMuted]);

  // Show tooltip only on the first entrance
  useEffect(() => {
    if (isVisible && !hasTriggeredTooltip.current) {
      setShowTooltip(true);
      hasTriggeredTooltip.current = true;
      const timer = setTimeout(() => setShowTooltip(false), 8000);
      return () => clearTimeout(timer);
    }
  }, [isVisible]);

  return (
    <AnimatePresence>
      {isVisible && (
        <div className="fixed bottom-6 right-6 z-[60] flex flex-col items-end gap-3">
          {/* Attention-Driving Tooltip */}
          <AnimatePresence>
            {showTooltip && (
              <motion.div
                initial={{ opacity: 0, y: 10, scale: 0.9 }}
                animate={{ opacity: 1, y: 0, scale: 1 }}
                exit={{ opacity: 0, y: 5, scale: 0.95 }}
                className="relative px-4 py-2 rounded-2xl bg-[var(--color-md-primary)] text-white text-[11px] font-black uppercase tracking-wider shadow-cinematic-3 border border-white/20 whitespace-nowrap"
              >
                Watch Live Walkthrough
                {/* Tooltip arrow */}
                <div className="absolute -bottom-1 right-6 w-2 h-2 bg-[var(--color-md-primary)] rotate-45 border-r border-b border-white/20" />
              </motion.div>
            )}
          </AnimatePresence>

          <motion.div
            initial={{ opacity: 0, scale: 0.8, x: 20, y: 20 }}
            animate={{ 
              opacity: 1, 
              scale: 1, 
              x: 0, 
              y: 0,
              boxShadow: showTooltip 
                ? [
                    "0 0 0 0 rgba(33, 150, 243, 0)",
                    "0 0 30px 4px rgba(33, 150, 243, 0.4)",
                    "0 0 0 0 rgba(33, 150, 243, 0)"
                  ] 
                : "0 20px 50px rgba(0,0,0,0.3)"
            }}
            transition={{ 
              type: 'spring', 
              damping: 25, 
              stiffness: 300,
              boxShadow: {
                repeat: showTooltip ? Infinity : 0,
                duration: 2,
                repeatDelay: 1
              }
            }}
            onMouseEnter={() => setIsHovered(true)}
            onMouseLeave={() => setIsHovered(false)}
            className="w-72 md:w-80 aspect-video rounded-xl overflow-hidden shadow-cinematic-4 border border-white/10 ring-1 ring-white/5 bg-[#0D1117] group cursor-pointer relative"
          >
            {/* Video Layer */}
            <video
              ref={videoRef}
              src={videoSrc}
              autoPlay
              loop
              muted={isMuted}
              playsInline
              className="w-full h-full object-cover"
              onClick={onExpand}
            />

            {/* Overlay Controls */}
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: isHovered ? 1 : 0 }}
              transition={{ duration: 0.2 }}
              className="absolute inset-0 bg-black/40 backdrop-blur-[2px] transition-opacity flex flex-col justify-between p-3 pointer-events-none"
            >
              <div className="flex justify-between items-start pointer-events-auto">
                <div className="flex items-center gap-2">
                  <button
                    onClick={(e) => {
                      e.stopPropagation();
                      setIsMuted(!isMuted);
                    }}
                    className="relative p-1.5 rounded-lg bg-black/50 hover:bg-black/70 text-white/90 transition-colors backdrop-blur-md border border-white/10 overflow-visible"
                    title={isMuted ? "Unmute" : "Mute"}
                  >
                    {isMuted ? <VolumeX size={16} /> : <Volume2 size={16} />}
                    {/* Visual Mute Signal (Sound Wave Pulse) */}
                    {isMuted && (
                      <motion.span 
                        animate={{ scale: [1, 1.3, 1], opacity: [0.5, 1, 0.5] }}
                        transition={{ repeat: Infinity, duration: 1.5 }}
                        className="absolute inset-0 rounded-lg ring-2 ring-[var(--color-md-primary)]/50 pointer-events-none"
                      />
                    )}
                  </button>

                  <AnimatePresence>
                    {isMuted && isHovered && (
                      <motion.span
                        initial={{ opacity: 0, x: -10 }}
                        animate={{ opacity: 1, x: 0 }}
                        exit={{ opacity: 0, x: -5 }}
                        className="text-[10px] font-black uppercase text-white/90 tracking-widest drop-shadow-md"
                      >
                        🔊 Tap for Sound
                      </motion.span>
                    )}
                  </AnimatePresence>
                </div>

                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onClose();
                  }}
                  className="p-1.5 rounded-lg bg-black/50 hover:bg-black/70 text-white/90 transition-colors backdrop-blur-md border border-white/10"
                  title="Dismiss"
                >
                  <X size={16} />
                </button>
              </div>

              <div className="flex justify-center pointer-events-auto">
                <button
                  onClick={(e) => {
                    e.stopPropagation();
                    onExpand();
                  }}
                  className="flex items-center gap-2 px-4 py-2 rounded-full bg-[var(--color-md-primary)] text-white text-xs font-black uppercase tracking-wider hover:scale-105 transition-transform shadow-lg"
                >
                  <Maximize2 size={14} />
                  Expand Demo
                </button>
              </div>
            </motion.div>
            
            {/* Small "Live" badge when not hovered */}
            {!isHovered && (
              <div className="absolute bottom-3 left-3 px-2 py-0.5 rounded bg-black/50 backdrop-blur-md border border-white/10 text-[10px] font-bold text-white/80 uppercase tracking-widest flex items-center gap-1.5 pointer-events-none">
                <span className="relative flex h-1.5 w-1.5">
                  <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-red-500 opacity-75"></span>
                  <span className="relative inline-flex rounded-full h-1.5 w-1.5 bg-red-500"></span>
                </span>
                Demo
              </div>
            )}
          </motion.div>
        </div>
      )}
    </AnimatePresence>
  );
}
